import React, { useEffect, useState } from 'react';
import {
    useAnalytics,
    useCurrentDebrief,
    useCurrentTracklog,
    useDebriefApp,
    useCurrentAircraft,
} from 'ff/debrief/hooks';
import { Segment, SegmentVrefOverride } from 'ff/debrief/models';
import TextField from 'ff/debrief/common/components/TextField';
import EditControls from 'ff/debrief/components/segments/EditControls';
import InputAdornment from '@mui/material/InputAdornment';
import Theme from 'ff/debrief/common/StyledComponentThemes';
import { convertSpeed, KTS_TO_MPH } from 'ff/debrief/utils';

const findSelectedApproach = (segment: Segment) => {
    const selected = segment?.instrumentApproaches?.find(
        iap => iap.iapSelected === true
    );
    return selected?.iapName ?? '';
};

interface Props {
    isEditMode: boolean;
    setIsEditMode: (isEditing: boolean) => void;
}

const VrefInputField = ({ isEditMode, setIsEditMode }: Props) => {
    const { isOwnTracklog } = useCurrentTracklog();
    const { selectedSegment } = useDebriefApp();
    const { saveVrefOverride, resetVrefOverride } = useCurrentDebrief();
    const { logVRefOverridden } = useAnalytics();
    const { speedUnit } = useCurrentAircraft();

    // Convert the vref value from kts to mph unit if needed
    const convertToDisplayUnit = (ktsValue: number) => {
        if (!ktsValue) return ktsValue;
        return speedUnit === 'mph' ? convertSpeed(ktsValue, 'mph') : ktsValue;
    };

    // Convert to kts for backend if needed
    const convertToKts = (displayValue: number) => {
        if (!displayValue) return displayValue;
        return speedUnit === 'mph'
            ? Math.round(displayValue / KTS_TO_MPH)
            : displayValue;
    };

    const [vrefValue, setVrefValue] = useState(
        convertToDisplayUnit(selectedSegment.approachParameters?.vref)
    );

    useEffect(() => {
        if (selectedSegment.approachParameters) {
            setVrefValue(
                convertToDisplayUnit(selectedSegment.approachParameters.vref)
            );
        }
    }, [selectedSegment.approachParameters, speedUnit]);

    const handleVrefChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!isEditMode) return;

        // Removes non-numerical input values
        const newValue = event.currentTarget.value.replace(/[^0-9.]/g, '');
        setVrefValue(newValue === '' ? null : parseFloat(newValue));
    };

    const createVrefOverride = (): SegmentVrefOverride => ({
        startEpochSecond: selectedSegment.startUtc,
        duration: selectedSegment.durationInSeconds,
        vref: convertToKts(vrefValue),
    });

    const handleSaveVref = () => {
        const ktsValue = convertToKts(vrefValue);
        if (ktsValue != selectedSegment.approachParameters.vref) {
            logVRefOverridden({
                approachType: findSelectedApproach(selectedSegment),
                originalValue: selectedSegment.approachParameters.vref,
                overrideValue: ktsValue,
            });
            saveVrefOverride(createVrefOverride());
        }
        setIsEditMode(false);
    };

    const handleResetVref = () => {
        resetVrefOverride(createVrefOverride());
        setIsEditMode(false);
    };

    const handleCancelEdit = () => {
        setVrefValue(
            convertToDisplayUnit(selectedSegment.approachParameters?.vref)
        );
        setIsEditMode(false);
    };

    if (!selectedSegment.approachParameters) return null;

    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
            }}
        >
            <TextField
                label={`Vref (${speedUnit || 'kts'}) - ${selectedSegment.approachParameters.vrefSource || ''} `}
                value={vrefValue || ''}
                onChange={handleVrefChange}
                slotProps={{
                    input: {
                        readOnly: !isEditMode,
                        disabled: !isOwnTracklog,
                        endAdornment: (
                            <InputAdornment position="end">
                                <EditControls
                                    disabled={!isOwnTracklog}
                                    isEditMode={isEditMode}
                                    onEditClick={() => setIsEditMode(true)}
                                    onSaveClick={handleSaveVref}
                                    onResetClick={handleResetVref}
                                    onCancelClick={handleCancelEdit}
                                    vrefSource={
                                        selectedSegment.approachParameters
                                            .vrefSource
                                    }
                                />
                            </InputAdornment>
                        ),
                    },
                }}
                sx={{
                    '& .MuiInputBase-input:focus': {
                        color: isEditMode ? Theme.blue : 'inherit',
                        fontWeight: isEditMode ? 'bold' : 'normal',
                    },
                }}
            />
        </div>
    );
};

export default VrefInputField;
